import { useContext, useState, useEffect } from 'react';

import {
  Checkbox,
  Stack,
  Paper,
  Container,
  Grid,
  TextField,
  Typography,
  Box,
  useTheme,
} from '@mui/material';

import {
  Cancel,
  Call,
  MobileFriendly,
  Store,
  GroupAdd,
  HourglassEmpty,
  CheckCircle,
  AccessAlarm,
  Timer,
} from '@mui/icons-material';

import { ChatsUser } from './ChartsUser';
import { ChartsDate } from './ChartsDate';
import ChartsRushHour from './ChartsRushHour';

import { AuthContext } from '../../context/Auth/AuthContext';
import useDashboard from '../../hooks/useDashboard';
import useContacts from '../../hooks/useContacts';

import moment from 'moment';

import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { getStyled } from './styles';

const Dashboard = () => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const [counters, setCounters] = useState({});
  const [dateFrom, setDateFrom] = useState(moment().format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(moment().format('YYYY-MM-DD'));
  const [finalizadas, setFinalizadas] = useState(true);
  const [canceladas, setCanceladas] = useState(false);

  const { find } = useDashboard();
  const { user } = useContext(AuthContext);
  const { count: contactsCount } = useContacts({ all: true });

  useEffect(() => {
    async function fetchData() {
      const data = await find({ date_from: dateFrom, date_to: dateTo });

      setCounters(data.counters);
    }

    fetchData();
  }, [dateFrom, dateTo, find]);

  function formatTime(minutes) {
    const duration = moment.duration(minutes, 'minutes');
    const hours = Math.floor(duration.asHours());
    const mins = duration.minutes();
    const secs = duration.seconds();

    return `${hours.toString().padStart(2, '0')}h ${mins.toString().padStart(2, '0')}m ${secs.toString().padStart(2, '0')}s`;
  }

  const handleFinalizadas = (event) => {
    if (canceladas) {
      setFinalizadas(event.target.checked);
    } else {
      setFinalizadas(event.target.checked);
      setCanceladas(true);
    }
  };

  const handleCanceladas = (event) => {
    if (finalizadas) {
      setCanceladas(event.target.checked);
    } else {
      setFinalizadas(true);
      setCanceladas(event.target.checked);
    }
  };

  function renderFilters() {
    return (
      <Stack
        direction='row'
        spacing={2}
        alignItems='center'
        flexWrap='nowrap'
        minWidth={600}
      >
        <Box sx={{ flex: 1 }}>
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale='pt-br'
          >
            <DatePicker
              value={dateFrom ? moment(dateFrom) : null}
              onChange={(newValue) => {
                setDateFrom(
                  newValue ? moment(newValue).format('YYYY-MM-DD') : null,
                );
              }}
              label='Data Inicial'
              format='DD/MM/YYYY'
              enableAccessibleFieldDOMStructure={false}
              slots={{ textField: TextField }}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  sx: theme.custom.textField,
                },
              }}
            />
          </LocalizationProvider>
        </Box>
        <Box sx={{ flex: 1 }}>
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale='pt-br'
          >
            <DatePicker
              value={dateTo ? moment(dateTo) : null}
              onChange={(newValue) => {
                setDateTo(
                  newValue ? moment(newValue).format('YYYY-MM-DD') : null,
                );
              }}
              label='Data Final'
              format='DD/MM/YYYY'
              enableAccessibleFieldDOMStructure={false}
              slots={{ textField: TextField }}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  sx: theme.custom.textField,
                },
              }}
            />
          </LocalizationProvider>
        </Box>
        <div style={classes.checkBoxField}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={finalizadas}
              onChange={handleFinalizadas}
              sx={{
                padding: 0,
                color:
                  theme.palette.mode === 'dark'
                    ? '#FFF'
                    : theme.palette.primary.main,
                '&.Mui-checked': {
                  color:
                    theme.palette.mode === 'dark'
                      ? '#FFF'
                      : theme.palette.primary.main,
                },
              }}
            />
            <Typography
              style={classes.checkBox}
              component='h4'
              variant='h6'
              sx={{
                color:
                  theme.palette.mode === 'dark'
                    ? '#FFF'
                    : theme.palette.primary.main,
              }}
            >
              Atendimentos Finalizados
            </Typography>
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={canceladas}
              onChange={handleCanceladas}
              sx={{
                padding: 0,
                color:
                  theme.palette.mode === 'dark'
                    ? '#FFF'
                    : theme.palette.primary.main,
                '&.Mui-checked': {
                  color:
                    theme.palette.mode === 'dark'
                      ? '#FFF'
                      : theme.palette.primary.main,
                },
              }}
            />
            <Typography
              style={classes.checkBox}
              component='h4'
              variant='h6'
              sx={{
                color:
                  theme.palette.mode === 'dark'
                    ? '#FFF'
                    : theme.palette.primary.main,
              }}
            >
              Atendimentos Cancelados
            </Typography>
          </div>
        </div>
      </Stack>
    );
  }

  return (
    <Container maxWidth='lg' style={classes.container}>
      <Grid container spacing={3}>
        {/* Cards Section */}
        <Grid container size={{ xs: 12 }} spacing={3}>
          {/* Conexões Ativas */}
          {user.super && (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Paper sx={classes.card}>
                <div style={classes.cardContent}>
                  <div style={classes.cardText}>
                    <Typography variant='subtitle2' sx={classes.cardTitle}>
                      Conexões Ativas
                    </Typography>
                    <Typography variant='h4' sx={classes.cardValue}>
                      {counters.totalWhatsappSessions || 0}
                    </Typography>
                  </div>
                  <MobileFriendly sx={classes.cardIcon} />
                </div>
              </Paper>
            </Grid>
          )}

          {/* Empresas */}
          {user.super && (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Paper sx={classes.card}>
                <div style={classes.cardContent}>
                  <div style={classes.cardText}>
                    <Typography variant='subtitle2' sx={classes.cardTitle}>
                      Empresas
                    </Typography>
                    <Typography variant='h4' sx={classes.cardValue}>
                      {counters.totalCompanies || 0}
                    </Typography>
                  </div>
                  <Store sx={classes.cardIcon} />
                </div>
              </Paper>
            </Grid>
          )}

          {/* Em Conversa */}
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Paper sx={classes.card}>
              <div style={classes.cardContent}>
                <div style={classes.cardText}>
                  <Typography variant='subtitle2' sx={classes.cardTitle}>
                    Em Conversa
                  </Typography>
                  <Typography variant='h4' sx={classes.cardValue}>
                    {counters.supportHappening || 0}
                  </Typography>
                </div>
                <Call sx={classes.cardIcon} />
              </div>
            </Paper>
          </Grid>

          {/* Aguardando */}
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Paper sx={classes.card}>
              <div style={classes.cardContent}>
                <div style={classes.cardText}>
                  <Typography variant='subtitle2' sx={classes.cardTitle}>
                    Aguardando
                  </Typography>
                  <Typography variant='h4' sx={classes.cardValue}>
                    {counters.supportPending || 0}
                  </Typography>
                </div>
                <HourglassEmpty sx={classes.cardIcon} />
              </div>
            </Paper>
          </Grid>

          {/* Novos Contatos */}
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Paper sx={classes.card}>
              <div style={classes.cardContent}>
                <div style={classes.cardText}>
                  <Typography variant='subtitle2' sx={classes.cardTitle}>
                    Contatos
                  </Typography>
                  <Typography variant='h4' sx={classes.cardValue}>
                    {contactsCount || 0}
                  </Typography>
                </div>
                <GroupAdd sx={classes.cardIcon} />
              </div>
            </Paper>
          </Grid>

          {/* Tempo Médio de Conversa */}
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Paper sx={classes.card}>
              <div style={classes.cardContent}>
                <div style={classes.cardText}>
                  <Typography variant='subtitle2' sx={classes.cardTitle}>
                    Tempo Médio de Atendimentos
                  </Typography>
                  <Typography variant='h4' sx={classes.cardValue}>
                    {formatTime(counters.avgSupportTime || 0)}
                  </Typography>
                </div>
                <AccessAlarm sx={classes.cardIcon} />
              </div>
            </Paper>
          </Grid>

          {/* Finalizados */}
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Paper sx={classes.card}>
              <div style={classes.cardContent}>
                <div style={classes.cardText}>
                  <Typography variant='subtitle2' sx={classes.cardTitle}>
                    Finalizados
                  </Typography>
                  <Typography variant='h4' sx={classes.cardValue}>
                    {counters.supportFinished || 0}
                  </Typography>
                </div>
                <CheckCircle sx={classes.cardIcon} />
              </div>
            </Paper>
          </Grid>

          {/*Cancelados*/}
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Paper sx={classes.card}>
              <div style={classes.cardContent}>
                <div style={classes.cardText}>
                  <Typography variant='subtitle2' sx={classes.cardTitle}>
                    Cancelados
                  </Typography>
                  <Typography variant='h4' sx={classes.cardValue}>
                    {counters.supportCanceled || 0}
                  </Typography>
                </div>
                <Cancel sx={classes.cardIcon} />
              </div>
            </Paper>
          </Grid>

          {/* Tempo Médio de Espera */}
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Paper sx={classes.card}>
              <div style={classes.cardContent}>
                <div style={classes.cardText}>
                  <Typography variant='subtitle2' sx={classes.cardTitle}>
                    Tempo Médio Para Início dos Atendimentos
                  </Typography>
                  <Typography variant='h4' sx={classes.cardValue}>
                    {formatTime(counters.avgWaitTime || 0)}
                  </Typography>
                </div>
                <Timer sx={classes.cardIcon} />
              </div>
            </Paper>
          </Grid>
        </Grid>

        {/* Filters Section */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={classes.filterSection}>
            <Stack
              direction='row'
              spacing={2}
              alignItems='center'
              flexWrap='nowrap'
              sx={{
                minWidth: '100%',
                display: 'flex',
                justifyContent: 'space-evenly',
              }}
            >
              <Stack
                direction='row'
                spacing={2}
                alignItems='center'
                flexWrap='nowrap'
                minWidth={600}
              >
                {renderFilters()}
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* Charts Section */}
        <Grid size={{ xs: 12 }}>
          <Paper style={classes.fixedHeightPaper}>
            <ChatsUser
              dateFrom={dateFrom}
              dateTo={dateTo}
              finalizadas={finalizadas}
              canceladas={canceladas}
            />
          </Paper>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper style={classes.fixedHeightPaper}>
            <ChartsDate
              dateFrom={dateFrom}
              dateTo={dateTo}
              finalizadas={finalizadas}
              canceladas={canceladas}
            />
          </Paper>
        </Grid>

        <ChartsRushHour
          dateFrom={dateFrom}
          dateTo={dateTo}
          finalizadas={finalizadas}
          canceladas={canceladas}
        />
      </Grid>
    </Container>
  );
};

export default Dashboard;
